import { AsyncPipe, DecimalPipe } from '@angular/common'
import { ChangeDetectionStrategy, Component, inject } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormsModule } from '@angular/forms'
import { MatFormField, MatLabel } from '@angular/material/form-field'
import { MatButton } from '@angular/material/button'
import { MatIcon } from '@angular/material/icon'
import { MatOption, MatSelect } from '@angular/material/select'
import { ActivatedRoute, Router } from '@angular/router'
import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import { ChartConfiguration } from 'chart.js'
import { BaseChartDirective } from 'ng2-charts'
import { combineLatest, distinctUntilChanged, filter, firstValueFrom, map, shareReplay, switchMap } from 'rxjs'
import { ClientsService } from '../clients.service'
import { MetricsService, SlaTeamBreakdown, SlaProcessingDistributionBucket } from '../metrics.service'
import { MultiFilterComponent } from '../multi-filter/multi-filter.component'
import { TeamsService } from '../teams.service'
import { AppTableColumn, TableComponent } from '../table/table.component'
import { SpinnerComponent } from '../spinner/spinner.component'
import { ToolbarComponent } from '../toolbar/toolbar.component'
import { UsersService } from '../users.service'
import { getColorPalette, isEqual } from '../utils'
import { themeColor } from '../theme-color'

// The picker only needs enough of a team to identify and label it, which lets the client-scoped and
// the environment-wide sources feed the same filter.
export type SelectableTeam = { id: number; name: string; clientName: string }

// The same "<client> / <team>" label the support pages use. It earns its keep only for a support user,
// whose picker spans every client and would otherwise show several identically-named teams; an admin
// never sees past their own client, so the prefix would repeat one name down the whole list.
export function describeSelectableTeam(team: SelectableTeam, includeClientName: boolean): string {
  return includeClientName ? `${team.clientName} / ${team.name}` : team.name
}

// Grouped by client so the shared prefix reads as a heading rather than as repetition scattered down the list.
function byClientThenTeam(first: SelectableTeam, second: SelectableTeam): number {
  return first.clientName.localeCompare(second.clientName) || first.name.localeCompare(second.name)
}

const SELECTABLE_MONTH_COUNT = 12

const MONTH_NAME_KEYS = ['JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE', 'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER']

export type SlaMonthOption = {
  value: string
  nameKey: string
  year: number
}

/**
 * Months are cut on UTC boundaries on the backend, so the options are built from UTC parts too. Deriving
 * them from the local date would offer a month the backend rejects for the couple of hours around a month
 * change when the two calendars disagree.
 */
export function buildSlaMonthOptions(now: Date): SlaMonthOption[] {
  return Array.from({ length: SELECTABLE_MONTH_COUNT }, (_unused, index) => {
    const monthStart = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - index, 1))
    const year = monthStart.getUTCFullYear()
    const monthOfYear = monthStart.getUTCMonth() + 1
    return {
      value: `${year}-${String(monthOfYear).padStart(2, '0')}`,
      nameKey: MONTH_NAME_KEYS[monthStart.getUTCMonth()],
      year,
    }
  })
}

@Component({
  selector: 'app-sla-dashboard',
  standalone: true,
  imports: [
    AsyncPipe,
    BaseChartDirective,
    DecimalPipe,
    FormsModule,
    MatButton,
    MatFormField,
    MatIcon,
    MatLabel,
    MatOption,
    MatSelect,
    MultiFilterComponent,
    TableComponent,
    SpinnerComponent,
    ToolbarComponent,
    TranslatePipe,
  ],
  templateUrl: './sla-dashboard.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './sla-dashboard.component.scss',
})
export class SlaDashboardComponent {
  route = inject(ActivatedRoute)
  router = inject(Router)
  clientsService = inject(ClientsService)
  metricsService = inject(MetricsService)
  teamsService = inject(TeamsService)
  usersService = inject(UsersService)
  translateService = inject(TranslateService)

  monthOptions = buildSlaMonthOptions(new Date())

  client$ = this.clientsService.getCurrentUserClient().pipe(shareReplay(1))

  currentUser$ = this.usersService.getCurrentUser().pipe(
    filter((result) => result.isSuccess),
    map((result) => result.data ?? null),
    filter((user) => user !== null),
    shareReplay(1)
  )

  isSupportUser$ = this.currentUser$.pipe(
    map((user) => user.role === 'SUPPORT'),
    shareReplay(1)
  )

  // The picker invokes its label callbacks as bare functions rather than through a binding, so the role has
  // to be readable synchronously rather than piped in from the template.
  private isSupportUser = false

  private trackRole = this.isSupportUser$.pipe(takeUntilDestroyed()).subscribe((isSupportUser) => (this.isSupportUser = isSupportUser))

  // The client endpoint returns the teams without repeating the client on each one, so the name is
  // taken from the client itself.
  private teamsOfClient$ = this.client$.pipe(
    filter((result) => result.isSuccess),
    map((result): SelectableTeam[] =>
      (result.data?.teams ?? []).map((team) => ({
        id: team.id,
        name: team.name,
        clientName: result.data?.name ?? '',
      }))
    ),
    shareReplay(1)
  )

  // Support users report on any team, not just the one their own account happens to sit under. Admins
  // stay inside their own client, which the endpoint enforces regardless of what the picker offers.
  teams$ = this.isSupportUser$.pipe(
    switchMap((isSupportUser) =>
      isSupportUser
        ? this.teamsService.getAllTeams().pipe(
            filter((result) => result.isSuccess),
            map((result): SelectableTeam[] =>
              (result.data ?? []).map((team) => ({
                id: team.id,
                name: team.name,
                clientName: team.client.name,
              }))
            )
          )
        : this.teamsOfClient$
    ),
    map((teams) => [...teams].sort(byClientThenTeam)),
    shareReplay(1)
  )

  // Deduped because the whole parameter map is upstream: the table's own sorting is in the URL now,
  // and a sort press must not re-run the month's figures.
  private selectedTeamIdsFromUrl$ = this.route.queryParamMap.pipe(
    map((params) =>
      params
        .getAll('teamIds')
        .map((id) => parseInt(id))
        .filter((id) => !isNaN(id))
    ),
    distinctUntilChanged((previous, current) => isEqual(previous, current))
  )

  // An empty selection falls back to the viewer's own client rather than to everything the picker offers: a
  // support user opening the page wants their own client's figures, not every client in the environment summed
  // into one meaningless total.
  teamIds$ = combineLatest([this.teams$, this.teamsOfClient$, this.selectedTeamIdsFromUrl$]).pipe(
    map(([selectableTeams, teamsOfClient, selectedIds]) => {
      const validSelectedIds = selectedIds.filter((id) => selectableTeams.some((team) => team.id === id))
      return validSelectedIds.length > 0 ? validSelectedIds : teamsOfClient.map((team) => team.id)
    }),
    shareReplay(1)
  )

  selectedTeamIds$ = combineLatest([this.teams$, this.selectedTeamIdsFromUrl$]).pipe(
    map(([teams, selectedIds]) => selectedIds.filter((id) => teams.some((team) => team.id === id)))
  )

  selectedMonth$ = this.route.queryParamMap.pipe(
    map((params) => {
      const month = params.get('month')
      return this.monthOptions.some((option) => option.value === month) ? month! : this.monthOptions[0].value
    }),
    distinctUntilChanged(),
    shareReplay(1)
  )

  slaMetrics$ = combineLatest([this.teamIds$, this.selectedMonth$]).pipe(
    switchMap(([teamIds, month]) => this.metricsService.getSlaMetrics(teamIds, month)),
    shareReplay(1)
  )

  processingDistributionChart$ = this.slaMetrics$.pipe(
    filter((result) => result.isSuccess),
    map((result): ChartConfiguration<'bar'>['data'] => {
      const buckets = result.data?.processingDistribution ?? []
      return {
        labels: buckets.map((bucket, index) => this.describeDistributionBucket(buckets, bucket, index)),
        datasets: [
          {
            data: buckets.map((bucket) => bucket.meetingCount),
            label: this.translateService.instant('SLA_DASHBOARD.MEETINGS_AXIS'),
            backgroundColor: getColorPalette(1)[0],
          },
        ],
      }
    })
  )

  processingDistributionChartOptions: ChartConfiguration<'bar'>['options'] = {
    responsive: true,
    scales: {
      x: { grid: { display: false }, ticks: { color: themeColor('loading-gray') } },
      y: { beginAtZero: true, grid: { color: themeColor('border') }, ticks: { precision: 0, color: themeColor('loading-gray') } },
    },
    plugins: {
      legend: { display: false },
    },
  }

  readonly teamColumns: AppTableColumn<SlaTeamBreakdown>[] = [
    {
      id: 'teamName',
      header: 'COMMON.TEAM',
      value: (team) => team.teamName,
      tone: 'strong',
      sortable: true,
      autoFit: true,
    },
    {
      id: 'completedMeetingCount',
      header: 'SLA_DASHBOARD.COMPLETED_MEETINGS',
      value: (team) => team.volume.completedMeetingCount,
      numeric: true,
      sortable: true,
    },
    {
      id: 'completedMeetingHours',
      header: 'SLA_DASHBOARD.TOTAL_HOURS',
      value: (team) => Math.round(team.volume.completedMeetingDurationInSeconds / 3600),
      numeric: true,
      sortable: true,
    },
    {
      id: 'erroredMeetingCount',
      header: 'SLA_DASHBOARD.ERRORED_MEETINGS',
      value: (team) => team.volume.erroredMeetingCount,
      numeric: true,
      sortable: true,
    },
    {
      id: 'erroredMeetingPercentage',
      header: 'SLA_DASHBOARD.ERROR_RATE',
      value: (team) => team.volume.erroredMeetingPercentage,
      display: (team) => `${team.volume.erroredMeetingPercentage.toFixed(1)} %`,
      numeric: true,
      sortable: true,
    },
    {
      id: 'medianProcessing',
      header: 'SLA_DASHBOARD.MEDIAN_PROCESSING_TIME',
      value: (team) => team.processing.medianInSeconds ?? 0,
      display: (team) => this.formatMinutes(team.processing.medianInSeconds),
      numeric: true,
      sortable: true,
    },
    {
      id: 'measuredMeetingCount',
      header: 'SLA_DASHBOARD.MEASURED_MEETINGS',
      value: (team) => team.processing.measuredMeetingCount,
      display: (team) => `${team.processing.measuredMeetingCount} / ${team.processing.meetingsWithAgendaCount}`,
      numeric: true,
    },
  ]

  teamRowId = (team: SlaTeamBreakdown): string => String(team.teamId)

  formatMinutes(durationInSeconds: number | null): string {
    if (durationInSeconds === null) {
      return '-'
    }
    return `${Math.round(durationInSeconds / 60)} ${this.translateService.instant('SLA_DASHBOARD.MINUTES_ABBREVIATION')}`
  }

  displayTeam = (team: SelectableTeam): string => describeSelectableTeam(team, this.isSupportUser)

  // The collapsed summary drops the client prefix once every selected team shares one, since repeating
  // it on each entry only makes the chip longer without telling the reader anything.
  displaySelectedTeams(teams: SelectableTeam[]) {
    return (ids: number[]) => {
      const selected = teams.filter((team) => ids.includes(team.id))
      const spansSeveralClients = new Set(selected.map((team) => team.clientName)).size > 1
      return selected.map((team) => describeSelectableTeam(team, this.isSupportUser && spansSeveralClients)).join(', ')
    }
  }

  isExporting = false

  async exportToPdf() {
    // Guarded because rendering runs a browser server side: a double click would launch a second one for no
    // benefit, in a container that is already tight on memory.
    if (this.isExporting) {
      return
    }
    this.isExporting = true
    try {
      // Resolving the selection is inside the try as well: if that stream fails, the flag still has to clear
      // or the button stays disabled for the rest of the visit.
      const [teamIds, month] = await firstValueFrom(combineLatest([this.teamIds$, this.selectedMonth$]))
      await firstValueFrom(this.metricsService.exportSlaMetrics(teamIds, month), { defaultValue: undefined })
    } finally {
      this.isExporting = false
    }
  }

  async onMonthChange(month: string) {
    await this.router.navigate([], { queryParams: { month }, queryParamsHandling: 'merge', relativeTo: this.route })
  }

  async onTeamChange(selectedIds: number[]) {
    await this.router.navigate([], {
      queryParams: selectedIds.length ? { teamIds: selectedIds } : { teamIds: null },
      queryParamsHandling: 'merge',
      relativeTo: this.route,
    })
  }

  async onTeamClear() {
    await this.router.navigate([], {
      queryParams: { teamIds: null },
      queryParamsHandling: 'merge',
      relativeTo: this.route,
    })
  }

  private describeDistributionBucket(buckets: SlaProcessingDistributionBucket[], bucket: SlaProcessingDistributionBucket, index: number): string {
    const lowerBound = index === 0 ? null : buckets[index - 1].upToSeconds
    if (lowerBound === null) {
      return `< ${this.formatBoundary(bucket.upToSeconds)}`
    }
    if (bucket.upToSeconds === null) {
      return `> ${this.formatBoundary(lowerBound)}`
    }
    return `${this.formatBoundary(lowerBound)} – ${this.formatBoundary(bucket.upToSeconds)}`
  }

  private formatBoundary(boundaryInSeconds: number | null): string {
    if (boundaryInSeconds === null) {
      return ''
    }
    if (boundaryInSeconds < 3600) {
      return `${boundaryInSeconds / 60} ${this.translateService.instant('SLA_DASHBOARD.MINUTES_ABBREVIATION')}`
    }
    return `${boundaryInSeconds / 3600} ${this.translateService.instant('SLA_DASHBOARD.HOURS_ABBREVIATION')}`
  }
}
